import { Injectable } from '@angular/core';
import { ArtToothForDiagram, ArtToothTreatment, ArtTreatmentDefinition, ArtTreatmentIntegrity } from '../models';
import { ART_PRESET_TOOTH_IDS } from '../constants/art-treatment.constants';

const PRESET_TOOTH_IDS: ReadonlySet<number> = new Set(ART_PRESET_TOOTH_IDS);

type ArtTreatmentTooth = Pick<ArtToothForDiagram, 'toothId' | 'artTreatment'>;

@Injectable({ providedIn: 'root' })
export class ArtTreatmentRulesService {
	readonly definitionMapping = new Map<ArtTreatmentIntegrity, ArtTreatmentDefinition[]>([
		[ArtTreatmentIntegrity.Adjacent, [ArtTreatmentDefinition.Unmovable, ArtTreatmentDefinition.AttachmentRestricted]],
		[ArtTreatmentIntegrity.RestorationPlanned, [ArtTreatmentDefinition.Unmovable, ArtTreatmentDefinition.AttachmentRestricted]],
		[ArtTreatmentIntegrity.Missing, [ArtTreatmentDefinition.ExtractionPlanned]],
		[ArtTreatmentIntegrity.MissingRestorationPlanned, [ArtTreatmentDefinition.ExtractionPlanned]],
		[ArtTreatmentIntegrity.PreviouslyRestored, [ArtTreatmentDefinition.Unmovable, ArtTreatmentDefinition.AttachmentRestricted]]
	]);

	getAvailableIntegrity(): ArtTreatmentIntegrity[] {
		return [
			ArtTreatmentIntegrity.RestorationPlanned,
			ArtTreatmentIntegrity.Missing,
			ArtTreatmentIntegrity.MissingRestorationPlanned,
			ArtTreatmentIntegrity.PreviouslyRestored
		];
	}

	getEnabledIntegrity(selectedTeeth: ArtToothForDiagram[]): ArtTreatmentIntegrity[] {
		return selectedTeeth.length > 0 ? this.getAvailableIntegrity() : [];
	}

	getAvailableDefinitions(): ArtTreatmentDefinition[] {
		return [ArtTreatmentDefinition.ExtractionPlanned, ArtTreatmentDefinition.Unmovable, ArtTreatmentDefinition.AttachmentRestricted];
	}

	getDefinitionsForIntegrity(integrity: ArtTreatmentIntegrity): ArtTreatmentDefinition[] {
		return this.definitionMapping.get(integrity) ?? [];
	}

	getAllowedDefinitions(selectedTeeth: ArtTreatmentTooth[]): ArtTreatmentDefinition[] {
		if (selectedTeeth.length === 0) {
			return [];
		}

		const definitionsByTooth = selectedTeeth.map(tooth =>
			this.getDefinitionsForIntegrity(tooth.artTreatment?.integrity ?? ArtTreatmentIntegrity.Adjacent)
		);

		const commonDefinitions = this.getAvailableDefinitions().filter(definition =>
			definitionsByTooth.every(definitions => definitions.includes(definition))
		);

		return selectedTeeth.every(tooth => PRESET_TOOTH_IDS.has(tooth.toothId))
			? commonDefinitions.filter(definition => definition !== ArtTreatmentDefinition.Unmovable)
			: commonDefinitions;
	}

	getSelectedDefinitions(selectedTeeth: ArtTreatmentTooth[]): ArtTreatmentDefinition[] {
		if (selectedTeeth.length === 0) {
			return [];
		}

		return this.getAvailableDefinitions().filter(definition =>
			selectedTeeth.every(tooth => tooth.artTreatment?.definitions.includes(definition))
		);
	}

	getPartiallySelectedDefinitions(selectedTeeth: ArtTreatmentTooth[]): ArtTreatmentDefinition[] {
		if (selectedTeeth.length === 0) {
			return [];
		}

		return this.getAvailableDefinitions().filter(definition => {
			const selectedCount = selectedTeeth.filter(tooth => tooth.artTreatment?.definitions.includes(definition)).length;

			return selectedCount > 0 && selectedCount < selectedTeeth.length;
		});
	}

	getTreatmentsAfterIntegrityChange(
		selectedTeeth: ArtTreatmentTooth[],
		integrity: ArtTreatmentIntegrity
	): Readonly<Record<number, ArtToothTreatment>> {
		return Object.fromEntries(
			selectedTeeth.map(tooth => [
				tooth.toothId,
				{
					integrity,
					definitions: this.filterDefinitionsByIntegrity(tooth.artTreatment, integrity, tooth.toothId)
				}
			])
		);
	}

	getTreatmentsAfterDefinitionToggle(
		selectedTeeth: ArtTreatmentTooth[],
		definition: ArtTreatmentDefinition
	): Readonly<Record<number, ArtToothTreatment>> {
		if (definition === ArtTreatmentDefinition.Unmovable && selectedTeeth.every(tooth => PRESET_TOOTH_IDS.has(tooth.toothId))) {
			return {};
		}

		const isSelectedForAll = selectedTeeth.every(tooth => tooth.artTreatment?.definitions.includes(definition));

		return Object.fromEntries(
			selectedTeeth.map(tooth => {
				const treatment = tooth.artTreatment;
				const isPresetDefinition = definition === ArtTreatmentDefinition.Unmovable && PRESET_TOOTH_IDS.has(tooth.toothId);
				const isSelected = treatment?.definitions.includes(definition) ?? false;
				let definitions = treatment?.definitions ?? [];

				if (isSelectedForAll && isSelected && !isPresetDefinition) {
					definitions = definitions.filter(current => current !== definition);
				} else if (!isSelected) {
					definitions = [...definitions, definition];
				}

				return [tooth.toothId, { ...treatment, definitions }];
			})
		);
	}

	filterDefinitionsByIntegrity(
		treatment: ArtToothTreatment,
		integrity: ArtTreatmentIntegrity,
		toothId?: number
	): ArtTreatmentDefinition[] {
		const allowedDefinitions = this.getDefinitionsForIntegrity(integrity);
		const definitions = treatment.definitions.filter(definition => allowedDefinitions.includes(definition));

		return toothId != null && PRESET_TOOTH_IDS.has(toothId) && !definitions.includes(ArtTreatmentDefinition.Unmovable)
			? [ArtTreatmentDefinition.Unmovable, ...definitions]
			: definitions;
	}

	getEnabledDefinitions(
		selectedTeeth: ArtToothForDiagram[],
		disabledJawToothIds: ReadonlySet<number> = new Set<number>()
	): ArtTreatmentDefinition[] {
		const definitions = this.getAllowedDefinitions(selectedTeeth);

		return selectedTeeth.some(tooth => disabledJawToothIds.has(tooth.toothId))
			? definitions.filter(
					definition =>
						definition !== ArtTreatmentDefinition.Unmovable && definition !== ArtTreatmentDefinition.AttachmentRestricted
				)
			: definitions;
	}
}
